import * as Schema from "effect/Schema";
import { ThreadId, MessageId } from "./baseSchemas.ts";
import { NeucleosDoorContextSelection } from "./neucleosDoor.ts";

// LM5C, revised 18 September 2026 (Arc CLA): a prepared copy member is one
// exact World Canvas revision in the current representation — its manifest
// and complete file set by path and digest, inline text carried, object bytes
// retained on this Mac by digest — never a second authority.
export const OfflineCanvasFile = Schema.Struct({
  path: Schema.String,
  sha256: Schema.String,
  byteLength: Schema.Number,
  mediaType: Schema.String,
  storage: Schema.Literals(["inline", "object"]),
  text: Schema.optional(Schema.String),
});
export type OfflineCanvasFile = typeof OfflineCanvasFile.Type;
export const OfflineCanvas = Schema.Struct({
  memberId: Schema.String,
  canvasId: Schema.String,
  sequence: Schema.Number,
  revisionId: Schema.String,
  title: Schema.String,
  type: Schema.Literals(["canvas", "idea", "web-page", "website", "public-invitation"]),
  description: Schema.optional(Schema.String),
  files: Schema.Array(OfflineCanvasFile),
  /** Dependencies the preparation could not retain, named rather than dropped. */
  unsupported: Schema.Array(Schema.Struct({ path: Schema.String, reason: Schema.String })),
  digest: Schema.String,
});
export type OfflineCanvas = typeof OfflineCanvas.Type;
export const OfflineWorldCopy = Schema.Struct({
  copyId: Schema.String,
  worldKey: Schema.String,
  actorId: Schema.String,
  identity: Schema.String,
  grantId: Schema.String,
  grantExpiresAt: Schema.Number,
  preparedAt: Schema.Number,
  expiresAt: Schema.Number,
  selection: NeucleosDoorContextSelection,
  members: Schema.Array(OfflineCanvas),
  digest: Schema.String,
});
export type OfflineWorldCopy = typeof OfflineWorldCopy.Type;
export const OfflineWorldCopyReference = Schema.Struct({
  worldKey: Schema.String,
  copyId: Schema.String,
  copyDigest: Schema.String,
});
export type OfflineWorldCopyReference = typeof OfflineWorldCopyReference.Type;
// A Local message receipt records use of an already prepared World cut. It
// grants no World authority and is never a World ContextResolution.
export const OfflineContextReceipt = Schema.Struct({
  schemaVersion: Schema.Literal("local-offline-context-v1"),
  worldKey: Schema.String,
  copyId: Schema.String,
  copyDigest: Schema.String,
  threadId: ThreadId,
  messageId: MessageId,
  frozenAt: Schema.Number,
  expiresAt: Schema.Number,
  selectionCut: Schema.Number,
  selectionDigest: Schema.String,
  memberIds: Schema.Array(Schema.String),
  digest: Schema.String,
});
export type OfflineContextReceipt = typeof OfflineContextReceipt.Type;
// Arc CLA (register GOALS/GOALS-CANVAS-IDEA.html#cla): a Local draft in the
// current Canvas representation. Three kinds, kept distinct: an independent
// draft made on this Mac with no World coordinates; a derived draft edited
// from a dated prepared copy (its origin retained); and a legacy single-document
// draft saved by an earlier Cleo, readable but not editable. A draft's files are
// the revision's own file set: inline text hashed as saved, or a reference to a
// content-addressed local object whose bytes live under the state directory.
// No World standing lives in a draft; copying into a World is an explicit act.
export const LOCAL_DRAFT_OBJECT_ROUTE = "/api/neucleos/context-selection/offline/object";
export const LocalDraftType = Schema.Literals([
  "canvas",
  "idea",
  "web-page",
  "website",
  "public-invitation",
]);
export type LocalDraftType = typeof LocalDraftType.Type;
export const LocalDraftKind = Schema.Literals(["independent", "derived", "legacy-document"]);
export type LocalDraftKind = typeof LocalDraftKind.Type;
export const LocalDraftFile = Schema.Struct({
  path: Schema.String,
  sha256: Schema.String,
  byteLength: Schema.Number,
  mediaType: Schema.String,
  storage: Schema.Literals(["inline", "object"]),
  text: Schema.optional(Schema.String),
});
export type LocalDraftFile = typeof LocalDraftFile.Type;
export const LocalDraftFileInput = Schema.Union([
  Schema.Struct({ path: Schema.String, text: Schema.String }),
  Schema.Struct({ path: Schema.String, sha256: Schema.String }),
]);
export type LocalDraftFileInput = typeof LocalDraftFileInput.Type;
export const LocalDraftObject = Schema.Struct({
  sha256: Schema.String,
  byteLength: Schema.Number,
  mediaType: Schema.String,
  createdAt: Schema.Number,
});
export type LocalDraftObject = typeof LocalDraftObject.Type;
export const LocalDraftOrigin = Schema.Struct({
  copyId: Schema.String,
  copyDigest: Schema.String,
  memberId: Schema.String,
  canvasId: Schema.String,
  baseSequence: Schema.Number,
});
export type LocalDraftOrigin = typeof LocalDraftOrigin.Type;
export const LocalDraft = Schema.Struct({
  draftId: Schema.String,
  kind: LocalDraftKind,
  worldKey: Schema.NullOr(Schema.String),
  actorId: Schema.NullOr(Schema.String),
  origin: Schema.NullOr(LocalDraftOrigin),
  title: Schema.String,
  type: LocalDraftType,
  description: Schema.optional(Schema.String),
  files: Schema.Array(LocalDraftFile),
  headSequence: Schema.Number,
  requestKey: Schema.String,
  createdAt: Schema.Number,
  updatedAt: Schema.Number,
  digest: Schema.String,
});
export type LocalDraft = typeof LocalDraft.Type;
// CLA2 (register GOALS/GOALS-CANVAS-IDEA.html#cla2): a local draft copied into
// a World becomes a separate new Canvas; the Desk retains where the copy came
// from beside the World's receipt. One request key per intended copy.
export const LocalDraftCopyReceipt = Schema.Struct({
  draftId: Schema.String,
  requestKey: Schema.String,
  worldKey: Schema.String,
  canvasId: Schema.String,
  memberId: Schema.String,
  revisionId: Schema.String,
  sequence: Schema.Number,
  replayed: Schema.Boolean,
  origin: Schema.Struct({
    draftId: Schema.String,
    draftDigest: Schema.String,
    source: Schema.NullOr(LocalDraftOrigin),
  }),
  copiedAt: Schema.Number,
});
export type LocalDraftCopyReceipt = typeof LocalDraftCopyReceipt.Type;
export const LocalDraftListing = Schema.Struct({
  draftId: Schema.String,
  kind: LocalDraftKind,
  worldKey: Schema.NullOr(Schema.String),
  title: Schema.String,
  type: LocalDraftType,
  headSequence: Schema.Number,
  createdAt: Schema.Number,
  updatedAt: Schema.Number,
  digest: Schema.String,
});
export type LocalDraftListing = typeof LocalDraftListing.Type;

const inWorld = { worldKey: Schema.String };
export const OfflineCanvasPin = Schema.Struct({
  memberId: Schema.String,
  canvasId: Schema.String,
  sequence: Schema.Number,
});
export const OfflineWorldRequest = Schema.Union([
  Schema.Struct({
    action: Schema.Literal("availability"),
    worldKey: Schema.optional(Schema.String),
  }),
  Schema.Struct({
    action: Schema.Literal("list"),
    worldKey: Schema.optional(Schema.String),
    copyBefore: Schema.optional(Schema.Number),
    draftBefore: Schema.optional(Schema.Number),
  }),
  Schema.Struct({
    ...inWorld,
    action: Schema.Literal("prepare"),
    requestKey: Schema.String,
    threadId: ThreadId,
    pins: Schema.Array(OfflineCanvasPin),
    expectedSelectionDigest: Schema.String,
    expiresAt: Schema.Number,
  }),
  Schema.Struct({ ...inWorld, action: Schema.Literal("read"), copyId: Schema.String }),
  Schema.Struct({
    ...inWorld,
    action: Schema.Literal("readMember"),
    copyId: Schema.String,
    memberId: Schema.String,
  }),
  Schema.Struct({
    ...inWorld,
    action: Schema.Literal("readMemberFile"),
    copyId: Schema.String,
    memberId: Schema.String,
    sha256: Schema.String,
  }),
  // The derived draft of a prepared copy member (LM5C), in the current shape.
  Schema.Struct({
    ...inWorld,
    action: Schema.Literal("saveDraft"),
    copyId: Schema.String,
    memberId: Schema.String,
    requestKey: Schema.String,
    title: Schema.optional(Schema.String),
    description: Schema.optional(Schema.String),
    files: Schema.Array(LocalDraftFileInput),
  }),
  // CLA1: the independent draft. No World coordinates anywhere in the request.
  Schema.Struct({
    action: Schema.Literal("createDraft"),
    requestKey: Schema.String,
    title: Schema.String,
    type: LocalDraftType,
    description: Schema.optional(Schema.String),
    files: Schema.Array(LocalDraftFileInput),
  }),
  Schema.Struct({
    action: Schema.Literal("editDraft"),
    draftId: Schema.String,
    requestKey: Schema.String,
    title: Schema.optional(Schema.String),
    type: Schema.optional(LocalDraftType),
    description: Schema.optional(Schema.String),
    files: Schema.Array(LocalDraftFileInput),
  }),
  Schema.Struct({ action: Schema.Literal("readDraft"), draftId: Schema.String }),
  Schema.Struct({
    action: Schema.Literal("readDraftFile"),
    draftId: Schema.String,
    sha256: Schema.String,
  }),
  Schema.Struct({ action: Schema.Literal("discardDraft"), draftId: Schema.String }),
  Schema.Struct({
    action: Schema.Literal("copyToWorld"),
    draftId: Schema.String,
    worldKey: Schema.String,
    requestKey: Schema.String,
    title: Schema.optional(Schema.String),
  }),
  // LM5C: commit a derived draft as the next revision of its source Canvas,
  // only while the World's head still equals what the person last read.
  Schema.Struct({
    ...inWorld,
    action: Schema.Literal("updateSource"),
    draftId: Schema.String,
    requestKey: Schema.String,
    expectedHeadSequence: Schema.Number,
  }),
  Schema.Struct({ ...inWorld, action: Schema.Literal("forget"), copyId: Schema.String }),
]);
export type OfflineWorldRequest = typeof OfflineWorldRequest.Type;
export const OfflineWorldListing = Schema.Struct({
  copyIds: Schema.Array(Schema.String),
  draftIds: Schema.Array(Schema.String),
  copies: Schema.Array(
    Schema.Struct({
      copyId: Schema.String,
      preparedAt: Schema.Number,
      expiresAt: Schema.Number,
      selectionCut: Schema.Number,
      memberCount: Schema.Number,
    }),
  ),
  drafts: Schema.Array(LocalDraftListing),
  nextCopyCursor: Schema.NullOr(Schema.Number),
  nextDraftCursor: Schema.NullOr(Schema.Number),
});
export type OfflineWorldListing = typeof OfflineWorldListing.Type;
